import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Json } from "@/integrations/supabase/types";

export interface UnmatchedFeature {
  name: string;
  value: string;
  confidence: "Installed" | "Maybe Installed" | "Not Installed";
}

interface UnmatchedFeaturesDisplayProps {
  features: UnmatchedFeature[];
}

export function UnmatchedFeaturesDisplay({ features }: UnmatchedFeaturesDisplayProps) {
  if (!features?.length) return null;

  const getBadgeStyles = (confidence: UnmatchedFeature["confidence"]) => {
    switch (confidence) {
      case "Installed":
        return "bg-green-100 text-green-800";
      case "Not Installed":
        return "bg-red-100 text-red-800";
      case "Maybe Installed":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg font-semibold text-everest-teal">
          Additional NHTSA Safety Features
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-4">
            {features.map((feature, index) => (
              <div key={index} className="py-2">
                <div className="flex items-center justify-between">
                  <div className="font-medium text-everest-gray">
                    {feature.name}
                  </div>
                  <Badge 
                    variant="outline"
                    className={`text-xs ${getBadgeStyles(feature.confidence)}`}
                  >
                    {feature.confidence}
                  </Badge>
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  {feature.value}
                </div>
                <Separator className="mt-2" />
              </div>
            ))}
          </div>
          
          <div className="text-sm text-gray-600 italic mt-4">
            Note: These features were found in the NHTSA data but not matched in the ChromeData results. 
            This could be due to different naming conventions or feature categorizations between the data sources.
          </div>
        </div>
      </CardContent>
    </Card>
  );
}